import History from '@history';
import { createAsyncThunk, EntityId } from '@reduxjs/toolkit';
import { userDataSelector } from 'app/auth/store/user.selector';
import { routesUrl } from 'app/fuse-configs/navigationConfig';
import { hereLookup } from 'app/services/hereServices/here.api';
import jwtService from 'app/services/jwtService';
import { sendMedia } from 'app/services/mediaService/mediaService';
import { showMessage } from 'app/store/fuse/messageSlice';
import i18n from 'i18n';
import { selectUserFranchise } from '../../franchises/ducks/franchises.selector';
import { selectStores } from '../../stores/ducks/stores.selector';
import {
  createCommunicationCampaignTemplateApi,
  createCommunicationCampaignTemplateFromCampaignApi,
  deleteCampaignTemplateApi,
  getCampaignTemplateByIdApi,
  getCampaignTemplateListApi,
} from '../api/template.api';
import { CurrentCampaign, NotificationType } from '../campaign.type';
import { CommunicationCampaignTemplateOutDAO } from '../template.type';
import { toApiCampaignFilters } from '../utils/filterPayload';
import { detectFilterTypes } from '../utils/filterTypes';
import { currentCouponSelector, currentNewsSelector, fullCampaignSelector } from './notification.selector';
import {
  actions,
  resetCurrent,
  setAddressPostcode,
  setCampaignValidationDialog,
  setCurrentCampaignFiltersTypes,
  setFormPostcodeIsVisible,
  updateCurrentFullCampaign,
} from './notification.slice';

export const createNewTemplateThunk = createAsyncThunk(
  'campaignTemplate/createTemplate',
  async (_, { dispatch, getState }: { dispatch: any; getState: any }) => {
    try {
      const userId = jwtService.getUserId();

      if (!userId) {
        throw new Error('No user id');
      }
      const campaign = fullCampaignSelector(getState());
      const news = currentNewsSelector(getState());
      const coupon = currentCouponSelector(getState());
      const franchise = selectUserFranchise(getState());

      dispatch(setCampaignValidationDialog({ isLoading: true }));

      let s3Url: string | undefined;
      if (campaign.imageUrl) {
        const data = await fetch(campaign.imageUrl);
        const blob = await data.blob();
        s3Url = await sendMedia(blob);
      }

      const notificationCampaignDTO: CommunicationCampaignTemplateOutDAO = {
        name: campaign.communicationCampaign.name,
        type: campaign.communicationCampaign.type,
        averageBasket: campaign.communicationCampaign.averageBasket,
        notification: {
          title: campaign.communicationCampaign.notification.title,
          message: campaign.communicationCampaign.notification.message,
          imageUrl: s3Url || undefined,
          link: campaign.communicationCampaign?.notification.link || undefined,
        },
        news,
        coupon: coupon ?? undefined,
        campaignFilters: toApiCampaignFilters(campaign.communicationCampaign.campaignFilters),
        franchiseId: franchise.id,
        userId,
      };

      await createCommunicationCampaignTemplateApi(notificationCampaignDTO);
      dispatch(setCampaignValidationDialog({ hasBeenSaved: true }));
      dispatch(
        showMessage({ message: i18n.t('validation.successTemplate', { ns: 'communication' }), variant: 'success' }),
      );
      dispatch(actions.closeCampaignValidationDialog());
      History.push(routesUrl.campaignHistory);
      dispatch(resetCurrent());
    } catch (error: any) {
      if (error.response?.data?.detail === 'SENDER_ID_NOT_VALID')
        dispatch(
          showMessage({ message: i18n.t('validation.errorSenderId', { ns: 'communication' }), variant: 'error' }),
        );
      else
        dispatch(
          showMessage({ message: i18n.t('validation.errorTemplate', { ns: 'communication' }), variant: 'error' }),
        );
    } finally {
      dispatch(setCampaignValidationDialog({ isLoading: false }));
    }
  },
);

export const createNewTemplateFromCampaignThunk = createAsyncThunk(
  'campaignTemplate/createTemplateFromCampaign',
  async (
    { campaignId, name }: { campaignId: EntityId; name: string },
    { dispatch }: { dispatch: any; getState: any },
  ) => {
    try {
      await createCommunicationCampaignTemplateFromCampaignApi(campaignId, name);
      dispatch(
        showMessage({ message: i18n.t('validation.successTemplate', { ns: 'communication' }), variant: 'success' }),
      );
    } catch (_error: any) {
      dispatch(showMessage({ message: i18n.t('validation.errorTemplate', { ns: 'communication' }), variant: 'error' }));
    }
  },
);

export const getCampaignTemplateListThunk = createAsyncThunk(
  'campaignTemplate/getCampaignTemplateList',
  async (_, { dispatch }: { dispatch: any }) => {
    try {
      const templates = await getCampaignTemplateListApi();
      return templates;
    } catch (error: any) {
      dispatch(showMessage({ message: error.message, variant: 'error' }));
      return [];
    }
  },
);

export const getCampaignInfoFromTemplateThunk = createAsyncThunk(
  'campaignTemplate/getCampaignInfoFromTemplate',
  async (templateId: EntityId | null, { dispatch, getState }: { dispatch: any; getState: any }) => {
    try {
      dispatch(resetCurrent());
      if (templateId === null) {
        return;
      }
      const { id, ...template } = await getCampaignTemplateByIdApi(templateId);
      const user = userDataSelector(getState());
      const stores = selectStores(getState());
      template.campaignFilters.storeIds = stores
        .filter(store => template.type !== NotificationType.sms || user.stripeId || store.ctNum)
        .map(store => store.id);

      dispatch(setCurrentCampaignFiltersTypes(detectFilterTypes(template.campaignFilters)));

      if (template.campaignFilters.originProviderId !== null) {
        dispatch(setFormPostcodeIsVisible(true));
        const result = await hereLookup(template.campaignFilters.originProviderId);
        dispatch(
          setAddressPostcode({
            ...result,
            id: template.campaignFilters.originProviderId,
          }),
        );
      }

      const currentCampaign: CurrentCampaign = {
        communicationCampaign: {
          ...template,
          userId: String(template.userId),
          dateCreated: '',
        },
        news: {
          title: template.news?.title ?? template.notification.title,
          message: template.news?.message ?? template.notification.message,
          link: template.news?.link ?? template.notification.link,
          startDate: '',
          endDate: '',
          dateCreated: '',
        },
        coupon: template.coupon,
        isNewsDifferentFromCampaign: template.news?.message !== template.notification.message,
        hasCoupon: !!template.coupon,
        hasLink: !!template.notification.link,
        imageUrl: template.notification.imageUrl,
        isSMSLengthValid: template.type === NotificationType.sms,
        merchantUserId: '',
        isSent: false,
        isTemplate: false,
      };
      dispatch(updateCurrentFullCampaign(currentCampaign));
    } catch (error: any) {
      dispatch(showMessage({ message: error.message, variant: 'error' }));
    }
  },
);

export const deleteCampaignTemplateThunk = createAsyncThunk(
  'campaignTemplate/deleteCampaignTemplate',
  async (templateId: EntityId | null, { dispatch }: { dispatch: any }) => {
    try {
      if (templateId === null) {
        return;
      }
      await deleteCampaignTemplateApi(templateId);
      dispatch(
        showMessage({ message: i18n.t('create.deleteTemplate.success', { ns: 'communication' }), variant: 'success' }),
      );
      return templateId;
    } catch (error: any) {
      dispatch(showMessage({ message: error.message, variant: 'error' }));
    }
  },
);
